import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  HandCoins,
  Mail,
  Phone,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  PoundSterling,
  User,
  FileText,
  Activity,
  MessageSquare,
  Edit,
  Save,
  X,
  TrendingUp,
  History,
  Plus,
  Minus,
  ArrowRightLeft,
  UserPlus,
} from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import type { PromiseToPay, PromiseAuditLog } from '@/types/collections';
import { NextActionPanel } from './next-action-panel';
import { CompleteActionModal, type ActionData } from './complete-action-modal';
import { TransactionTypeBadge } from '@/components/transactions/transaction-type-badge';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/formatters';

interface PromiseDetailsDrawerProps {
  promise: PromiseToPay | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface EditablePromiseData {
  promiseDate: Date;
  promisedAmount: number;
  status: PromiseToPay['status'];
  confirmationMethod: PromiseToPay['confirmationMethod'];
  notes: string;
}

interface PromiseNote {
  id: string;
  content: string;
  createdBy: string;
  createdAt: Date;
  type: 'general' | 'call' | 'email' | 'system';
}

interface PromiseAction {
  id: string;
  type: 'email' | 'call' | 'task' | 'payment' | 'status_change';
  description: string;
  outcome?: string;
  createdBy: string;
  createdAt: Date;
}

// Unified activity type for the consolidated timeline
type UnifiedActivity = {
  id: string;
  type: 'email' | 'call' | 'task' | 'payment' | 'status_change' | 'note' | 'communication';
  tag: 'Activity' | 'Note' | 'Communication';
  title: string;
  description: string;
  outcome?: string;
  createdBy: string;
  createdAt: Date;
  noteType?: PromiseNote['type'];
};

export const PromiseDetailsDrawer: React.FC<PromiseDetailsDrawerProps> = ({ promise, open, onOpenChange }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<EditablePromiseData | null>(null);
  const [newNote, setNewNote] = useState('');
  const [completeActionModalOpen, setCompleteActionModalOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(['Activity', 'Note', 'Communication']);

  // Mock data for demonstration
  const mockNotes: PromiseNote[] = promise
    ? [
        {
          id: '1',
          content: promise.notes || 'Initial promise recorded via phone call. Customer confirmed payment date.',
          createdBy: promise.createdBy,
          createdAt: promise.createdAt,
          type: 'general',
        },
        {
          id: '2',
          content: 'Follow-up call made. Customer reconfirmed commitment to pay on promised date.',
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          type: 'call',
        },
        {
          id: '3',
          content: 'Reminder email sent to customer with payment details and invoice copies.',
          createdBy: 'System',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          type: 'email',
        },
      ]
    : [];

  const mockActions: PromiseAction[] = promise
    ? [
        {
          id: '1',
          type: 'status_change',
          description: 'Promise status changed to Active',
          createdBy: promise.createdBy,
          createdAt: promise.createdAt,
        },
        {
          id: '2',
          type: 'call',
          description: 'Outbound call made to customer',
          outcome: 'Customer confirmed payment commitment',
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        },
        {
          id: '3',
          type: 'email',
          description: 'Payment reminder email sent',
          outcome: 'Email delivered successfully',
          createdBy: 'System',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        },
      ]
    : [];

  // Mock audit log data - comprehensive promise modification history
  const mockAuditLogs: PromiseAuditLog[] = promise
    ? [
        {
          id: 'audit-1',
          promiseId: promise.id,
          action: 'status_change',
          description: 'Promise status changed',
          fieldName: 'status',
          oldValue: 'Active',
          newValue: promise.status,
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-2',
          promiseId: promise.id,
          action: 'amount_change',
          description: 'Promised amount updated',
          fieldName: 'promisedAmount',
          oldValue: promise.promisedAmount - 500,
          newValue: promise.promisedAmount,
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-3',
          promiseId: promise.id,
          action: 'date_change',
          description: 'Promise date updated',
          fieldName: 'promiseDate',
          oldValue: format(new Date(promise.promiseDate.getTime() - 7 * 24 * 60 * 60 * 1000), 'MMM dd, yyyy'),
          newValue: format(promise.promiseDate, 'MMM dd, yyyy'),
          createdBy: 'Mike Chen',
          createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-4',
          promiseId: promise.id,
          action: 'collection_reassignment',
          description: 'Promise reassigned to different collection case',
          oldCaseId: 'AR-00042',
          newCaseId: promise.caseId,
          oldCaseNumber: 'AR-00042',
          newCaseNumber: promise.caseNumber,
          createdBy: 'System',
          createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-5',
          promiseId: promise.id,
          action: 'field_update',
          description: 'Confirmation method updated',
          fieldName: 'confirmationMethod',
          oldValue: 'Email',
          newValue: promise.confirmationMethod,
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-6',
          promiseId: promise.id,
          action: 'note_added',
          description: 'Note added to promise',
          createdBy: 'Sarah Johnson',
          createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        },
        {
          id: 'audit-7',
          promiseId: promise.id,
          action: 'promise_created',
          description: 'Promise to pay created',
          createdBy: promise.createdBy,
          createdAt: promise.createdAt,
        },
      ]
    : [];

  // Create unified activities array combining actions and notes
  const unifiedActivities: UnifiedActivity[] = promise
    ? [
        // Add actions as Activity type
        ...mockActions.map((action) => ({
          id: action.id,
          type: action.type,
          tag: 'Activity' as const,
          title: action.description,
          description: action.description,
          outcome: action.outcome,
          createdBy: action.createdBy,
          createdAt: action.createdAt,
        })),
        // Add notes as Note type
        ...mockNotes.map((note) => ({
          id: note.id,
          type: 'note' as const,
          tag: 'Note' as const,
          title: 'Note Added',
          description: note.content,
          createdBy: note.createdBy,
          createdAt: note.createdAt,
          noteType: note.type,
        })),
      ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
    : [];

  // Filter activities based on selected tags
  const filteredActivities = selectedFilters.length === 0
    ? unifiedActivities
    : unifiedActivities.filter((activity) => selectedFilters.includes(activity.tag));

  // Initialize edit data when entering edit mode
  const handleEnterEditMode = () => {
    if (!promise) return;
    setEditedData({
      promiseDate: promise.promiseDate,
      promisedAmount: promise.promisedAmount,
      status: promise.status,
      confirmationMethod: promise.confirmationMethod,
      notes: promise.notes,
    });
    setIsEditMode(true);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData(null);
  };

  // Save changes
  const handleSaveChanges = () => {
    if (!editedData || !promise) return;

    // Validation
    if (editedData.promisedAmount <= 0) {
      toast.error('Promised amount must be greater than 0');
      return;
    }

    if (!editedData.promiseDate) {
      toast.error('Promise date is required');
      return;
    }

    // In a real application, this would call an API
    console.log('Saving changes:', editedData);
    toast.success('Promise details updated successfully');
    setIsEditMode(false);
    setEditedData(null);
  };

  // Add new note
  const handleAddNote = () => {
    if (!newNote.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    // In a real application, this would call an API
    console.log('Adding note:', newNote);
    toast.success('Note added successfully');
    setNewNote('');
  };

  // Handle complete action
  const handleCompleteAction = (actionData: ActionData) => {
    // In a real application, this would call an API
    console.log('Action completed:', actionData);
    toast.success('Action completed successfully');
    
    // You could update the promise state here if needed
    // For now, just log the action
  };

  // Get status badge
  const getStatusBadge = (status: PromiseToPay['status']) => {
    const variants: Record<PromiseToPay['status'], { className: string; icon: React.ReactNode }> = {
      Active: {
        className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      Broken: {
        className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        icon: <AlertCircle className="h-3 w-3" />,
      },
      Paid: {
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      'Partially Paid': {
        className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
        icon: <Clock className="h-3 w-3" />,
      },
    };
    return (
      <Badge className={`${variants[status].className} flex items-center gap-1 w-fit`}>
        {variants[status].icon}
        {status}
      </Badge>
    );
  };

  // Get confirmation method badge
  const getConfirmationMethodBadge = (method: PromiseToPay['confirmationMethod']) => {
    const icons = {
      Phone: <Phone className="h-3 w-3" />,
      Email: <Mail className="h-3 w-3" />,
      Portal: <TrendingUp className="h-3 w-3" />,
    };
    return (
      <Badge variant="outline" className="flex items-center gap-1 w-fit">
        {icons[method]}
        {method}
      </Badge>
    );
  };

  // Get action icon
  const getActionIcon = (type: PromiseAction['type']) => {
    const icons = {
      email: <Mail className="h-4 w-4 text-blue-600" />,
      call: <Phone className="h-4 w-4 text-green-600" />,
      task: <Calendar className="h-4 w-4 text-purple-600" />,
      payment: <PoundSterling className="h-4 w-4 text-emerald-600" />,
      status_change: <Activity className="h-4 w-4 text-amber-600" />,
    };
    return icons[type];
  };

  // Get note icon
  const getNoteIcon = (type: PromiseNote['type']) => {
    const icons = {
      general: <MessageSquare className="h-4 w-4 text-gray-600" />,
      call: <Phone className="h-4 w-4 text-green-600" />,
      email: <Mail className="h-4 w-4 text-blue-600" />,
      system: <Activity className="h-4 w-4 text-purple-600" />,
    };
    return icons[type];
  };

  // Get unified activity icon
  const getUnifiedActivityIcon = (activity: UnifiedActivity) => {
    if (activity.tag === 'Note' && activity.noteType) {
      return getNoteIcon(activity.noteType);
    }
    return getActionIcon(activity.type as PromiseAction['type']);
  };

  // Get audit log icon
  const getAuditLogIcon = (action: PromiseAuditLog['action']) => {
    switch (action) {
      case 'status_change':
        return ArrowRightLeft;
      case 'amount_change':
        return PoundSterling;
      case 'date_change':
        return Calendar;
      case 'collection_reassignment':
        return UserPlus;
      case 'field_update':
        return Edit;
      case 'note_added':
        return FileText;
      case 'promise_created':
        return History;
      default:
        return Activity;
    }
  };

  // Get audit log color
  const getAuditLogColor = (action: PromiseAuditLog['action']) => {
    switch (action) {
      case 'status_change':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'amount_change':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      case 'date_change':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
      case 'collection_reassignment':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'field_update':
        return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400';
      case 'note_added':
        return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
      case 'promise_created':
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    }
  };

  // Get tag color for unified activities
  const getTagColor = (tag: UnifiedActivity['tag']) => {
    switch (tag) {
      case 'Activity':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
      case 'Note':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800';
      case 'Communication':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
  };

  // Get icon background color for unified activities
  const getIconBackgroundColor = (tag: UnifiedActivity['tag']) => {
    switch (tag) {
      case 'Activity':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
      case 'Note':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
      case 'Communication':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedFilters(['Activity', 'Note', 'Communication']);
  };

  const activeFilterCount = 3 - selectedFilters.length;

  if (!promise) return null;

  const displayData = isEditMode && editedData ? editedData : promise;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[1100px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <SheetTitle className="flex items-center gap-2 text-2xl">
                <HandCoins className="h-6 w-6 text-primary" />
                Promise Details
              </SheetTitle>
              <SheetDescription className="mt-1">
                Payment commitment from {promise.customerName}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              {isEditMode ? (
                <>
                  <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveChanges}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={handleEnterEditMode}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          {/* Promise ID Badge */}
          <div className="mt-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg border border-primary/20">
              <span className="text-sm text-muted-foreground">Promise ID:</span>
              <span className="text-sm font-semibold text-primary">{promise.id}</span>
            </div>
          </div>

          {/* Edit Mode Indicator */}
          {isEditMode && (
            <div className="mt-3 flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
              <Edit className="h-4 w-4" />
              <span className="font-medium">Edit Mode Active</span>
              <span className="text-muted-foreground">- Make changes and click Save to update</span>
            </div>
          )}
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-4 border-b shrink-0">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="transactions">
                Transactions
              </TabsTrigger>
              <TabsTrigger value="activity" className="flex items-center gap-2">
                Activity
                <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5">
                  {unifiedActivities.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="audit">
                Audit Log
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1 h-full">
            <ScrollAreaViewport className="h-full w-full">
              {/* Overview Tab */}
              <TabsContent value="overview" className="px-6 py-4 space-y-6">
              {/* Two-Column Layout: Customer Information (Left) and Promise Summary (Right) */}
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Customer Information Panel - LEFT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Customer Information
                    </CardTitle>
                    <CardDescription>Contact details and account information</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Two-column grid layout */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      {/* Customer Name - Left column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Customer Name</p>
                        <p className="text-sm font-semibold">{promise.customerName}</p>
                      </div>

                      {/* Account Number - Right column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Account Number</p>
                        <p className="text-sm font-mono">{promise.customerAccountNumber}</p>
                      </div>

                      {/* Email - Left column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          Email Address
                        </Label>
                        <a
                          href={`mailto:${promise.customerEmail}`}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline block truncate"
                          title={promise.customerEmail}
                        >
                          {promise.customerEmail}
                        </a>
                      </div>

                      {/* Phone - Right column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Phone Number
                        </Label>
                        <a 
                          href={`tel:${promise.customerPhone}`} 
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {promise.customerPhone}
                        </a>
                      </div>

                      {/* Broken Promises - Left column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <History className="h-3 w-3" />
                          Broken Promises
                        </p>
                        <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                          {promise.brokenPromisesCount || 0}
                        </p>
                      </div>

                      {/* Promise Success Rate - Right column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">Promise Success Rate</p>
                        <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {promise.brokenPromisesCount
                            ? Math.round((1 / (promise.brokenPromisesCount + 1)) * 100)
                            : 100}
                          %
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Promise Summary Panel - RIGHT */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <HandCoins className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      Promise Summary
                    </CardTitle>
                    <CardDescription>Key details about this payment commitment</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Two-column grid layout */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      {/* Promise Date - Left column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Promise Date
                        </Label>
                        {isEditMode ? (
                          <Input
                            type="date"
                            value={format(editedData!.promiseDate, 'yyyy-MM-dd')}
                            onChange={(e) =>
                              setEditedData({ ...editedData!, promiseDate: new Date(e.target.value) })
                            }
                            className="h-8"
                          />
                        ) : (
                          <p className="text-sm font-semibold">{format(displayData.promiseDate, 'MMM dd, yyyy')}</p>
                        )}
                      </div>

                      {/* Promised Amount - Right column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <PoundSterling className="h-3 w-3" />
                          Promised Amount
                        </Label>
                        {isEditMode ? (
                          <Input
                            type="number"
                            value={editedData!.promisedAmount}
                            onChange={(e) =>
                              setEditedData({ ...editedData!, promisedAmount: parseFloat(e.target.value) || 0 })
                            }
                            className="h-8"
                            min="0"
                            step="0.01"
                          />
                        ) : (
                          <p className="text-sm font-bold">{formatCurrency(displayData.promisedAmount)}</p>
                        )}
                      </div>

                      {/* Status - Left column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          Status
                        </Label>
                        {isEditMode ? (
                          <Select
                            value={editedData!.status}
                            onValueChange={(value) =>
                              setEditedData({ ...editedData!, status: value as PromiseToPay['status'] })
                            }
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Active">Active</SelectItem>
                              <SelectItem value="Broken">Broken</SelectItem>
                              <SelectItem value="Paid">Paid</SelectItem>
                              <SelectItem value="Partially Paid">Partially Paid</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div>{getStatusBadge(displayData.status)}</div>
                        )}
                      </div>

                      {/* Confirmation Method - Right column */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Confirmation Method
                        </Label>
                        {isEditMode ? (
                          <Select
                            value={editedData!.confirmationMethod}
                            onValueChange={(value) =>
                              setEditedData({
                                ...editedData!,
                                confirmationMethod: value as PromiseToPay['confirmationMethod'],
                              })
                            }
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Phone">Phone</SelectItem>
                              <SelectItem value="Email">Email</SelectItem>
                              <SelectItem value="Portal">Portal</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <div>{getConfirmationMethodBadge(displayData.confirmationMethod)}</div>
                        )}
                      </div>

                      {/* Created By - Left column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <User className="h-3 w-3" />
                          Created By
                        </p>
                        <p className="text-sm font-semibold">{promise.createdBy}</p>
                      </div>

                      {/* Created At - Right column */}
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Created At
                        </p>
                        <p className="text-sm font-semibold">{format(promise.createdAt, 'MMM dd, yyyy HH:mm')}</p>
                      </div>

                      {/* Days Overdue - Spans 2 columns if present */}
                      {promise.daysOverdue && promise.daysOverdue > 0 && (
                        <div className="col-span-2 space-y-1">
                          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Days Overdue
                          </p>
                          <Badge variant="destructive" className="text-sm px-2 py-0.5 w-fit">
                            {promise.daysOverdue} days
                          </Badge>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Next Action Panel */}
              <NextActionPanel 
                promise={promise}
                onCompleteAction={() => setCompleteActionModalOpen(true)}
              />

              {/* Notes Section - Full Width Below */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    Promise Notes
                  </CardTitle>
                  <CardDescription>
                    {isEditMode ? "Edit promise notes or view full history in the Activity tab" : "Quick view of promise notes"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isEditMode ? (
                    <Textarea
                      value={editedData!.notes}
                      onChange={(e) => setEditedData({ ...editedData!, notes: e.target.value })}
                      placeholder="Add notes about this promise..."
                      rows={4}
                      className="resize-none"
                    />
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm leading-relaxed">
                        {displayData.notes || 'No notes available'}
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setActiveTab("activity")}
                        className="gap-2"
                      >
                        <History className="h-4 w-4" />
                        View Full Activity History
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              </TabsContent>

              {/* Transactions Tab */}
              <TabsContent value="transactions" className="px-6 py-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Outstanding Transactions
                  </CardTitle>
                  <CardDescription>
                    All transactions associated with this promise ({promise.invoices.length} total)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reference Number</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Days Overdue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {promise.invoices.map((invoice) => {
                        // Calculate days overdue based on invoice date and current date
                        const daysOverdue = invoice.currentStatus === 'Unpaid' 
                          ? Math.max(0, Math.floor((new Date().getTime() - invoice.invoiceDate.getTime()) / (1000 * 60 * 60 * 24)) - 30)
                          : 0;
                        
                        return (
                          <TableRow key={invoice.id}>
                            <TableCell className="font-medium">
                              {invoice.invoiceNumber}
                            </TableCell>
                            <TableCell>
                              {format(invoice.invoiceDate, 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell>
                              {format(new Date(invoice.invoiceDate.getTime() + 30 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy')}
                            </TableCell>
                            <TableCell>
                              <TransactionTypeBadge type="invoice" />
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(invoice.originalAmount)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(invoice.balanceRemaining)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  invoice.currentStatus === 'Unpaid'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                              >
                                {invoice.currentStatus}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {daysOverdue > 0 ? (
                                <span className="text-red-600 dark:text-red-400 font-medium">
                                  {daysOverdue}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Outstanding:</span>
                    <span className="text-xl font-bold text-red-600 dark:text-red-400">
                      {formatCurrency(promise.invoices.reduce((sum, inv) => sum + inv.balanceRemaining, 0))}
                    </span>
                  </div>
                </CardContent>
              </Card>
              </TabsContent>

              {/* Activity Tab - Consolidated view of Actions and Notes */}
              <TabsContent value="activity" className="px-6 py-4 space-y-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <CardTitle className="text-lg">Activity Timeline</CardTitle>
                        <CardDescription>
                          Complete history of all activities and notes
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {filteredActivities.length} {filteredActivities.length === 1 ? 'item' : 'items'}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Filter Controls */}
                    <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <Activity className="h-4 w-4" />
                          Filter by Type
                        </Label>
                        {activeFilterCount > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="h-7 text-xs"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Clear Filters ({activeFilterCount})
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant={selectedFilters.includes('Activity') ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            if (selectedFilters.includes('Activity')) {
                              setSelectedFilters(selectedFilters.filter(f => f !== 'Activity'));
                            } else {
                              setSelectedFilters([...selectedFilters, 'Activity']);
                            }
                          }}
                          className={cn(
                            "h-8",
                            selectedFilters.includes('Activity') && 
                            "bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700"
                          )}
                        >
                          <Activity className="h-4 w-4 mr-2" />
                          Activity
                        </Button>
                        <Button
                          variant={selectedFilters.includes('Note') ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            if (selectedFilters.includes('Note')) {
                              setSelectedFilters(selectedFilters.filter(f => f !== 'Note'));
                            } else {
                              setSelectedFilters([...selectedFilters, 'Note']);
                            }
                          }}
                          className={cn(
                            "h-8",
                            selectedFilters.includes('Note') && 
                            "bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-600 dark:hover:bg-purple-700"
                          )}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Notes
                        </Button>
                        <Button
                          variant={selectedFilters.includes('Communication') ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            if (selectedFilters.includes('Communication')) {
                              setSelectedFilters(selectedFilters.filter(f => f !== 'Communication'));
                            } else {
                              setSelectedFilters([...selectedFilters, 'Communication']);
                            }
                          }}
                          className={cn(
                            "h-8",
                            selectedFilters.includes('Communication') && 
                            "bg-green-600 hover:bg-green-700 text-white dark:bg-green-600 dark:hover:bg-green-700"
                          )}
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Communications
                        </Button>
                      </div>
                    </div>

                    {/* Add New Note Section */}
                    <Separator />
                    <div className="space-y-3 p-4 border rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900">
                      <Label htmlFor="newNote" className="text-sm font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Add New Note
                      </Label>
                      <Textarea
                        id="newNote"
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Enter your note here..."
                        rows={4}
                        className="resize-none"
                      />
                      <div className="flex justify-end">
                        <Button
                          onClick={handleAddNote}
                          disabled={!newNote.trim()}
                          size="sm"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Add Note
                        </Button>
                      </div>
                    </div>

                    {/* Timeline Display */}
                    {filteredActivities.length > 0 ? (
                      <>
                        <Separator />
                        <div className="space-y-4">
                          {filteredActivities.map((activity, index) => (
                            <div key={activity.id} className="relative">
                              {index !== filteredActivities.length - 1 && (
                                <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                              )}
                              <div className="flex gap-4">
                                <div className={cn(
                                  "flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0",
                                  getIconBackgroundColor(activity.tag)
                                )}>
                                  {getUnifiedActivityIcon(activity)}
                                </div>
                                <div className="flex-1 space-y-2">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 space-y-2">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <Badge
                                          variant="outline"
                                          className={cn("text-xs font-medium", getTagColor(activity.tag))}
                                        >
                                          {activity.tag}
                                        </Badge>
                                        {activity.noteType && (
                                          <Badge variant="secondary" className="text-xs capitalize">
                                            {activity.noteType}
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-foreground leading-relaxed">
                                        {activity.description}
                                      </p>
                                      {activity.outcome && (
                                        <p className="text-sm text-muted-foreground italic">
                                          Outcome: {activity.outcome}
                                        </p>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground whitespace-nowrap">
                                      {format(activity.createdAt, "MMM d, yyyy h:mm a")}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      <User className="h-3 w-3 mr-1" />
                                      {activity.createdBy}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm font-medium">No activities match your filters</p>
                        <p className="text-xs mt-1">Try adjusting your filter selection</p>
                        {activeFilterCount > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={clearFilters}
                            className="mt-4"
                          >
                            Clear All Filters
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit" className="px-6 py-4 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      Audit Log
                    </CardTitle>
                    <CardDescription>
                      Complete chronological history of all promise modifications and changes
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {mockAuditLogs.map((log, index) => {
                        const Icon = getAuditLogIcon(log.action);
                        const colorClass = getAuditLogColor(log.action);
                        
                        return (
                          <div key={log.id} className="relative">
                            {index !== mockAuditLogs.length - 1 && (
                              <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                            )}
                            <div className="flex gap-4">
                              <div className={cn(
                                "flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0",
                                colorClass
                              )}>
                                <Icon className="h-4 w-4" />
                              </div>
                              <div className="flex-1 space-y-2">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 space-y-1">
                                    <p className="font-medium text-sm">{log.description}</p>
                                    
                                    {/* Status/Amount/Date/Field Changes */}
                                    {(log.action === 'status_change' || log.action === 'amount_change' || log.action === 'date_change' || log.action === 'field_update') && log.oldValue && log.newValue && (
                                      <div className="flex items-center gap-2 text-sm">
                                        <Badge variant="outline" className="text-xs">
                                          {log.action === 'amount_change' && typeof log.oldValue === 'number' 
                                            ? formatCurrency(log.oldValue) 
                                            : log.oldValue}
                                        </Badge>
                                        <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
                                        <Badge variant="outline" className="text-xs">
                                          {log.action === 'amount_change' && typeof log.newValue === 'number' 
                                            ? formatCurrency(log.newValue) 
                                            : log.newValue}
                                        </Badge>
                                      </div>
                                    )}
                                    
                                    {/* Collection Reassignment */}
                                    {log.action === 'collection_reassignment' && log.oldCaseNumber && log.newCaseNumber && (
                                      <div className="text-sm text-muted-foreground space-y-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs">From:</span>
                                          <Badge variant="outline" className="text-xs font-mono">
                                            {log.oldCaseNumber}
                                          </Badge>
                                          <ArrowRightLeft className="h-3 w-3" />
                                          <span className="text-xs">To:</span>
                                          <Badge variant="outline" className="text-xs font-mono">
                                            {log.newCaseNumber}
                                          </Badge>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                                    {format(log.createdAt, "MMM d, yyyy h:mm a")}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    <User className="h-3 w-3 mr-1" />
                                    {log.createdBy}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {mockAuditLogs.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No audit log entries found for this promise.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" className="z-10" />
          </ScrollArea>
        </Tabs>
      </SheetContent>

      {/* Complete Action Modal */}
      {promise && (
        <CompleteActionModal
          promise={promise}
          open={completeActionModalOpen}
          onOpenChange={setCompleteActionModalOpen}
          onActionComplete={handleCompleteAction}
        />
      )}
    </Sheet>
  );
};
